"use client";

import {
  defaultTransitionDurationMs,
  Modal as UIModal,
  type ModalProps as UIModalProps,
} from "@kyan/ui";
import { atom, useAtom } from "jotai";
import { type ReactNode, useEffect, useState } from "react";

import { getJotaiStore } from "../getJotaiStore";

export const renderNodeOrFunction = (
  nodeOrFunction: ReactNodeOrFunction,
  handleClose: () => void,
): ReactNode => {
  return typeof nodeOrFunction === "function"
    ? nodeOrFunction({ handleClose })
    : nodeOrFunction;
};

export type ReactNodeOrFunction =
  | ReactNode
  | ((args: { handleClose: () => void }) => ReactNode);

interface ModalProps extends Omit<
  UIModalProps,
  "open" | "header" | "footer" | "children" | "onOpenChange"
> {
  onCleanup: () => void;
  header?: ReactNodeOrFunction;
  footer?: ReactNodeOrFunction;
  children: ReactNodeOrFunction;
  timeout?: number;
}

export type ShowModalOptions = Omit<ModalProps, "onCleanup">;

const Modal: React.FC<ModalProps> = ({
  header,
  footer,
  children,
  onCleanup,
  timeout,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(true);

  const handleClose = () => {
    setIsOpen(false);
  };

  useEffect(() => {
    if (!timeout) return;

    const timeoutId = setTimeout(() => {
      setIsOpen(false);
    }, timeout);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [timeout]);

  useEffect(() => {
    if (!isOpen) onCleanup();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- we want to run this effect only when isOpen changes
  }, [isOpen]);

  return (
    <UIModal
      open={isOpen}
      footer={renderNodeOrFunction(footer, handleClose)}
      header={renderNodeOrFunction(header, handleClose)}
      onOpenChange={setIsOpen}
      {...props}
    >
      {renderNodeOrFunction(children, handleClose)}
    </UIModal>
  );
};

const modalAtom = atom<ShowModalOptions | null>(null);

const store = getJotaiStore();

export const showModal = (options: ShowModalOptions) => {
  store.set(modalAtom, options);

  // Return cleanup function
  return () => {
    store.set(modalAtom, null);
  };
};

export const ModalRenderer: React.FC = () => {
  const [modal, setModal] = useAtom(modalAtom);

  const cleanup = () => {
    // Delay the cleanup to allow the modal to close smoothly
    setTimeout(() => {
      setModal(null);
    }, defaultTransitionDurationMs);
  };

  if (!modal) return null;

  return <Modal onCleanup={cleanup} {...modal} />;
};
